import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { NavLink, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BookOpen, ShoppingCart, Layers, Tag, Droplets, Boxes, Wifi, WifiOff, RefreshCw, LogOut, KeyRound, ChevronDown, BadgePercent, Truck, GitBranch, Settings2, Lock, Receipt, Users, Percent, BarChart3, Target, Bell, Download, Menu, X, Wallet } from "lucide-react";

// Botón "Instalar RubiPOS": aparece solo cuando el navegador permite instalar la PWA
function InstalarBtn() {
  const [listo, setListo] = useState(!!window.__rubiposInstall);
  useEffect(() => { const f = () => setListo(true); window.addEventListener("rubipos-instalable", f); return () => window.removeEventListener("rubipos-instalable", f); }, []);
  if (!listo || window.matchMedia("(display-mode: standalone)").matches) return null;
  return (
    <button data-testid="instalar-app-button" onClick={async () => { const e = window.__rubiposInstall; if (!e) return; e.prompt(); const r = await e.userChoice; if (r?.outcome === "accepted") { window.__rubiposInstall = null; setListo(false); } }} className="h-8 w-8 2xl:w-auto 2xl:px-3 rounded-full bg-brand-amber text-brand-ink text-[11px] font-bold flex items-center justify-center gap-1.5 hover:bg-brand-amber-dark transition-colors" title="Instalar RubiPOS en esta PC">
      <Download className="w-3.5 h-3.5" /> <span className="hidden min-[1800px]:inline">Instalar</span>
    </button>
  );
}
import { useAuth } from "@/context/AuthContext";
import AvisoMorosidad from "@/components/AvisoMorosidad";
import api, { formatApiError } from "@/lib/api";
import { getQueue, isForcedOffline, setForcedOffline, syncQueue } from "@/lib/offline";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import NotificacionesBell from "@/components/NotificacionesBell";

const NAV = [
  { to: "/", id: "venta", label: "Venta", icon: ShoppingCart, permiso: "venta" },
  { to: "/mis-ventas", id: "mis-ventas", label: "Mis ventas", icon: BarChart3, permiso: "mis_ventas" },
  { to: "/gastos", id: "gastos", label: "Gastos", icon: Receipt, permiso: "gastos" },
  { to: "/promociones", id: "promociones", label: "Promos", icon: BadgePercent, permiso: "promociones" },
  { to: "/resurtido", id: "resurtido", label: "Resurtido", icon: RefreshCw, permiso: "resurtido" },
  { to: "/stock", id: "stock", label: "Stock", icon: Boxes, permiso: "inventario" },
  { to: "/catalogo", id: "catalogo", label: "Catálogo & PACK", icon: Layers, permiso: "catalogo" },
  { to: "/mermas", id: "mermas", label: "Mermas", icon: Droplets, permiso: "mermas" },
  { to: "/traspasos", id: "traspasos", label: "Traspasos", icon: Truck, permiso: "traspasos" },
  { to: "/pedidos-pagos", id: "pedidos-pagos", label: "Pedidos y pagos", icon: Wallet, permiso: "traspasos" },
  { to: "/trazabilidad", id: "trazabilidad", label: "Trazabilidad", icon: GitBranch, permiso: "trazabilidad" },
  { to: "/cortes", id: "cortes", label: "Corte", icon: Lock, permiso: "cortes" },
  { to: "/clientes", id: "clientes", label: "Clientes", icon: Users, permiso: "clientes" },
  { to: "/comisiones", id: "comisiones", label: "Comisiones", icon: Percent, permiso: "comisiones" },
  { to: "/reportes", id: "reportes", label: "Reportes", icon: BarChart3, permiso: "reportes" },
  { to: "/metas", id: "metas", label: "Metas", icon: Target, permiso: "metas" },
  { to: "/alertas", id: "alertas", label: "Alertas", icon: Bell, permiso: "alertas" },
  { to: "/usuarios", id: "usuarios", label: "Equipo", icon: Settings2, permiso: "usuarios" },
  { to: "/ayuda", id: "ayuda", label: "Ayuda", icon: BookOpen },
];

const ROL_LABEL = { gran_admin: "Gran Admin", coordinador: "Coordinador", gerente_propia: "Gerente", gerente_inversionista: "Gerente inversionista", franquiciatario: "Franquiciatario", gerente_franquicia: "Gerente franquicia", ejecutiva: "Ejecutiva" };

export default function HeaderNav() {
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const [online, setOnline] = useState(navigator.onLine);
  const [forced, setForced] = useState(isForcedOffline());
  const [queueLen, setQueueLen] = useState(getQueue().length);
  const [syncing, setSyncing] = useState(false);
  const [switchOpen, setSwitchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [usuarios, setUsuarios] = useState([]);
  const [switchEmail, setSwitchEmail] = useState("");
  const [switchPass, setSwitchPass] = useState("");
  const [soloTurno, setSoloTurno] = useState(true);

  useEffect(() => {
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    const onQueue = () => setQueueLen(getQueue().length);
    const onForced = () => setForced(isForcedOffline());
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    window.addEventListener("rubipos-queue", onQueue);
    window.addEventListener("rubipos-offline", onForced);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("rubipos-queue", onQueue);
      window.removeEventListener("rubipos-offline", onForced);
    };
  }, []);

  const offline = forced || !online;

  useEffect(() => {
    if (!offline && queueLen > 0) handleSync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offline]);

  async function handleSync() {
    if (syncing || getQueue().length === 0) return;
    setSyncing(true);
    try {
      const r = await syncQueue(api);
      if (r.synced > 0) toast.success(`${r.synced} operación(es) sincronizadas`);
      if (r.failed > 0) toast.error(`${r.failed} operación(es) no se pudieron sincronizar`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSyncing(false);
      setQueueLen(getQueue().length);
    }
  }

  async function openSwitch() {
    setSwitchOpen(true);
    setSwitchPass("");
    setSoloTurno(true);
    try {
      const { data } = await api.get("/auth/turno-usuarios");
      setUsuarios(data);
      const enTurno = data.filter((u) => u.en_turno);
      if (enTurno.length === 1) setSwitchEmail(enTurno[0].email);
      else if (!enTurno.length) setSoloTurno(false);
    } catch {}
  }

  async function doSwitch(e) {
    e.preventDefault();
    try {
      const { data } = await api.post("/auth/turno", { email: switchEmail, credencial: switchPass });
      setUser(data.user);
      toast.success(`Turno de ${data.user.nombre}${data.con_pin ? " (PIN)" : ""}`);
      setSwitchOpen(false);
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  const iniciales = (user?.nombre || "?").split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]).join("").toUpperCase();

  return (
    <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-brand-line" data-testid="header-nav">
      <AvisoMorosidad />
      <div className="flex items-center gap-2 px-3 sm:px-4 h-14">
        <button data-testid="mobile-menu-button" onClick={() => setMenuOpen(true)} className="md:hidden w-9 h-9 -ml-1 rounded-full flex items-center justify-center text-brand-ink hover:bg-brand-surface transition-colors" title="Menú"><Menu className="w-5 h-5" /></button>
        <NavLink to="/" className="flex items-center shrink-0" data-testid="brand-link">
          <img src="/rubipos-logo-t.png" alt="RubiPOS" className="h-8 w-auto" />
        </NavLink>
        <div className="hidden md:block h-6 w-px bg-brand-line" />

        <nav className="hidden md:flex items-center gap-0.5 overflow-x-auto scrollbar-none min-w-0 flex-1">
          {NAV.filter((n) => (!n.permiso || (user?.permisos || []).includes(n.permiso))).map(({ to, id, label, icon: Icon }) => (
            <NavLink key={to} to={to} end={to === "/"} data-testid={`nav-${id}`} title={label} className={({ isActive }) => `tab-pill h-8 text-xs ${isActive ? "tab-pill-active px-3" : "px-2.5 2xl:px-2.5 hover:text-brand-ink"}`}>
              {({ isActive }) => (
                <>
                  <Icon className={`w-4 h-4 shrink-0 ${isActive ? "text-brand-amber" : ""}`} />
                  <span className={isActive ? "inline" : "hidden min-[1800px]:inline"}>{label}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-1.5 shrink-0">
          <InstalarBtn />
          <NotificacionesBell />
          <button
            data-testid="offline-toggle-button"
            onClick={() => setForcedOffline(!forced)}
            title={offline ? "Modo offline activado (clic para reconectar)" : "En línea (clic para simular offline)"}
            className={`relative flex items-center justify-center w-8 h-8 rounded-full border transition-colors duration-150 ${
              offline ? "bg-brand-amber-soft text-brand-amber-dark border-brand-amber-line" : "bg-white text-brand-graphite border-brand-line hover:border-brand-slate"
            }`}
          >
            <span className={`absolute top-0.5 right-0.5 w-2 h-2 rounded-full border border-white ${offline ? "bg-brand-amber" : "bg-emerald-500 text-emerald-500 dot-live"}`} />
            {offline ? <WifiOff className="w-3.5 h-3.5" /> : <Wifi className="w-3.5 h-3.5" />}
          </button>

          {queueLen > 0 && (
            <button
              data-testid="sync-queue-button"
              onClick={handleSync}
              disabled={offline || syncing}
              className="flex items-center gap-1.5 px-3 h-8 rounded-full text-xs font-semibold bg-brand-ink text-white disabled:opacity-60 transition-opacity duration-150"
            >
              <RefreshCw className={`w-3.5 h-3.5 text-brand-amber ${syncing ? "animate-spin" : ""}`} />
              {queueLen} pendiente{queueLen !== 1 ? "s" : ""}
            </button>
          )}

          <button
            data-testid="cashier-switch-button"
            onClick={openSwitch}
            className="flex items-center gap-1.5 pl-1 pr-2 h-8 rounded-full bg-white border border-brand-line hover:border-brand-amber transition-colors duration-150"
            title={`${user?.nombre} · ${ROL_LABEL[user?.rol] || user?.rol} · Cambio rápido de cajero`}
          >
            <span className="w-6 h-6 rounded-full bg-brand-amber text-brand-ink font-display font-extrabold text-[10px] flex items-center justify-center">{iniciales}</span>
            <span className="text-left leading-tight hidden lg:block">
              <span className="block text-[11px] font-semibold text-brand-ink max-w-[110px] 2xl:max-w-[160px] truncate">{(user?.nombre || "").split(" ").slice(0, 2).join(" ")}</span>
              <span className="hidden min-[1800px]:block text-[9px] uppercase tracking-wider text-brand-muted">{ROL_LABEL[user?.rol] || user?.rol}</span>
            </span>
            <ChevronDown className="w-3 h-3 text-brand-muted" />
          </button>

          <button
            data-testid="logout-button"
            onClick={async () => { await logout(); navigate("/login"); }}
            className="w-8 h-8 rounded-full border border-brand-line bg-white text-brand-ink flex items-center justify-center hover:bg-brand-ink hover:text-brand-amber hover:border-brand-ink transition-colors duration-150"
            title="Cerrar sesión"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>

      {menuOpen && createPortal(
        <div className="md:hidden fixed inset-0 z-[60]" data-testid="mobile-menu">
          <div className="absolute inset-0 bg-brand-ink/40 backdrop-blur-sm animate-fade-in" onClick={() => setMenuOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-[82vw] max-w-xs bg-white shadow-2xl flex flex-col animate-slide-in-left">
            <div className="flex items-center justify-between px-4 h-14 border-b border-brand-line">
              <img src="/rubipos-logo-t.png" alt="RubiPOS" className="h-7 w-auto" />
              <button data-testid="mobile-menu-close" onClick={() => setMenuOpen(false)} className="w-9 h-9 rounded-full flex items-center justify-center text-brand-muted hover:bg-brand-surface"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-4 py-3 border-b border-brand-line flex items-center gap-3">
              <span className="w-9 h-9 rounded-full bg-brand-amber text-brand-ink font-display font-extrabold text-xs flex items-center justify-center">{iniciales}</span>
              <div className="min-w-0"><div className="text-sm font-semibold text-brand-ink truncate">{user?.nombre}</div><div className="text-[10px] uppercase tracking-wider text-brand-muted">{ROL_LABEL[user?.rol] || user?.rol}</div></div>
            </div>
            <nav className="flex-1 overflow-y-auto py-2">
              {NAV.filter((n) => (!n.permiso || (user?.permisos || []).includes(n.permiso))).map(({ to, id, label, icon: Icon }) => (
                <NavLink key={to} to={to} end={to === "/"} data-testid={`mobile-nav-${id}`} onClick={() => setMenuOpen(false)} className={({ isActive }) => `flex items-center gap-3 mx-2 px-3 h-11 rounded-xl text-sm font-semibold transition-colors ${isActive ? "bg-brand-ink text-white" : "text-brand-slate hover:bg-brand-surface"}`}>
                  {({ isActive }) => (<><Icon className={`w-[18px] h-[18px] ${isActive ? "text-brand-amber" : ""}`} />{label}</>)}
                </NavLink>
              ))}
            </nav>
            <div className="p-3 border-t border-brand-line grid grid-cols-2 gap-2">
              <button data-testid="mobile-switch-button" onClick={() => { setMenuOpen(false); openSwitch(); }} className="btn-soft h-10 text-xs"><KeyRound className="w-3.5 h-3.5" /> Cambiar cajero</button>
              <button data-testid="mobile-logout-button" onClick={async () => { await logout(); navigate("/login"); }} className="btn-ink h-10 text-xs"><LogOut className="w-3.5 h-3.5 text-brand-amber" /> Salir</button>
            </div>
          </aside>
        </div>,
        document.body
      )}

      <Dialog open={switchOpen} onOpenChange={setSwitchOpen}>
        <DialogContent className="max-w-sm rounded-2xl" data-testid="cashier-switch-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold">Cambio rápido de cajero</DialogTitle>
          </DialogHeader>
          <form onSubmit={doSwitch} className="space-y-3">
            <div className="flex items-center justify-between text-[11px] text-brand-muted">
              <span data-testid="cashier-turno-count">{usuarios.filter((u) => u.en_turno).length} con entrada checada en RubiNova</span>
              <button type="button" data-testid="cashier-toggle-todos" onClick={() => setSoloTurno((v) => !v)} className="font-semibold text-brand-amber-dark hover:underline">{soloTurno ? "Mostrar todos" : "Solo con entrada checada"}</button>
            </div>
            <select data-testid="cashier-select" value={switchEmail} onChange={(e) => setSwitchEmail(e.target.value)} className="field" required>
              <option value="">Selecciona usuario…</option>
              {usuarios.filter((u) => !soloTurno || u.en_turno).map((u) => (
                <option key={u.id} value={u.email}>{u.en_turno ? "● " : ""}{u.nombre} — {ROL_LABEL[u.rol] || u.rol}{u.en_turno && u.ultimo_checkin ? ` · entrada ${new Date(u.ultimo_checkin).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}` : ""}</option>
              ))}
            </select>
            <div className="relative">
              <KeyRound className="w-4 h-4 absolute left-3 top-3 text-brand-muted" />
              <input data-testid="cashier-password-input" type="password" placeholder={usuarios.find((u) => u.email === switchEmail)?.tiene_pin ? "PIN o contraseña" : "Contraseña"} className="field pl-9" value={switchPass} onChange={(e) => setSwitchPass(e.target.value)} required />
            </div>
            <p className="text-[11px] text-brand-muted">Quien checa entrada en RubiNova aparece marcado con ●. Si el checador falla, puede iniciar turno con su PIN (preestablecido <strong>1234</strong>; se cambia en Equipo).</p>
            <button data-testid="cashier-switch-submit" type="submit" className="btn-amber w-full h-11">Iniciar turno</button>
          </form>
        </DialogContent>
      </Dialog>
    </header>
  );
}
